/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { API_BASE_URL } from "../../config/api";
import { useTenant } from "../../context/TenantContext";
import { getTokenRole, markAuthSessionActive, normalizeRole } from "../../utils/authUtils";
import { persistStudentSession, syncActiveLearner } from "../../utils/studentSession";

type SignInForm = {
  email: string;
  password: string;
};

export default function AuthPage() {
  const navigate = useNavigate();
  const { branding, tenant } = useTenant();

  const [formData, setFormData] = useState<SignInForm>({
    email: "",
    password: "",
  });

  const [message, setMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [logoError, setLogoError] = useState<boolean>(false);

  useEffect(() => {
    document.title = `Sign In | ${branding.displayName}`;
  }, [branding.displayName]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const routeByRole = (role?: string) => {
    if (localStorage.getItem("subscriptionBlocked") === "true") {
      navigate("/subscription-blocked");
      return;
    }

    const routes: Record<string, string> = {
      superadmin: "/superadmin",
      admin: "/admindashboard",
      headteacher: "/headteacher",
      teacher: "/teacherdashboard",
      student: "/dashboard",
    };

    const targetRoute = routes[normalizeRole(role)];
    if (!targetRoute) {
      console.error("Unknown role:", role);
      navigate("/dashboard");
      return;
    }
    navigate(targetRoute);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.token || !data?.user) {
        throw new Error(data?.message || "Invalid login credentials");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("authUserId", String(data.user.id));
      localStorage.setItem("userId", String(data.user.id));
      localStorage.setItem("userName", data.user.name ?? "");
      const normalizedRole = normalizeRole(data.user.role);
      const tokenRole = getTokenRole(data.token || "");
      const effectiveRole = normalizedRole || tokenRole;
      localStorage.setItem("role", effectiveRole || "");
      localStorage.setItem("profilePicture", data.user.profilePicture ?? "");
      localStorage.setItem("userTitle", data.user.title ?? "");
      localStorage.setItem("userCode", data.user.userCode ?? "");
      localStorage.setItem("email", data.user.email ?? "");

      if ((effectiveRole || data.user.role) === "student") {
        const linkedLearners = Array.isArray(data.linkedLearners) && data.linkedLearners.length > 0
          ? data.linkedLearners
          : [{
              id: data.user.id,
              name: data.user.name,
              email: data.user.email,
              classId: null,
              className: null,
              admissionNumber: null
            }];
        persistStudentSession(Number(data.user.id), linkedLearners);
        syncActiveLearner(linkedLearners[0]?.id || data.user.id);
      }

      if (data?.blockedAccess) {
        localStorage.setItem("subscriptionBlocked", "true");
        localStorage.setItem("subscriptionBlockedReason", data.blockedAccess.reason || "");
        localStorage.setItem("subscriptionBlockedMessage", data.blockedAccess.message || "");
      } else {
        localStorage.removeItem("subscriptionBlocked");
        localStorage.removeItem("subscriptionBlockedReason");
        localStorage.removeItem("subscriptionBlockedMessage");
      }
      markAuthSessionActive();

      setMessage("");
      setSuccessMessage("Login successful. Redirecting...");
      setTimeout(() => {
        routeByRole(effectiveRole || tokenRole || data.user.role);
      }, 1500);
    } catch (error: any) {
      setMessage(
        error?.message ||
        "We are unable to sign you in at this time. Please try again."
      );
      setFormData((prev) => ({ ...prev, password: "" }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-grow flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
          <div className="brand-surface-accent h-2" />

          <div className="p-6 sm:p-10">
            <div className="flex justify-center mb-4">
              {branding.logoUrl && !logoError ? (
                <img
                  src={branding.logoUrl}
                  alt={`${branding.displayName} logo`}
                  className="h-16 w-auto object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div className="brand-soft-bg flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 text-lg font-black text-slate-700">
                  {branding.initials}
                </div>
              )}
            </div>

            <h2 className="text-2xl sm:text-3xl font-extrabold text-center text-gray-800 mb-6">
              {branding.displayName}
            </h2>

            {message && (
              <div className="mb-5 p-3 rounded-md text-center text-sm font-medium bg-red-100 text-red-700">
                {message}
              </div>
            )}
            {tenant && !tenant.is_subscription_exempt && (tenant.status === "expired" || tenant.status === "suspended") && (
              <div className={`mb-5 p-3 rounded-md text-center text-sm font-medium ${tenant.status === "expired" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
                {tenant.status === "expired"
                  ? "This school subscription has expired. Sign in to review billing access or contact your administrator."
                  : "This school account is currently suspended. Contact your administrator or support for assistance."}
              </div>
            )}
            {successMessage && (
              <div className="mb-5 p-3 rounded-md text-center text-sm font-medium bg-green-100 text-green-700">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email or Phone Number
                </label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="name@example.com or 097xxxxxxx"
                  className="brand-input-focus w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                    className="brand-input-focus w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="brand-link text-sm hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="brand-button-primary w-full font-bold py-3 rounded-full shadow-md transition"
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                Need access? Contact your school administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
